import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getRequest, postDirect, postRequest, ResponseDataType } from 'api';
import { Table, Spin, Card, Form, Button, Modal, Input, Select, Tag, Checkbox } from 'antd';
import { toast } from 'react-toastify';
import './BillInfo.css';

interface StudentData {
  name: string;
  dob: string;
  gender: number;
  email: string;
  address: string;
  cmnd: string;
  bhytNum: string;
  studentCode: string;
}

interface ParentData {
  parentId: number;
  name: string;
  phoneNumber: string;
  email: string;
}

interface InvoiceDetail {
  studentInvoiceId: number;
  studentId: number;
  classYearId: number;
  studentCode: string | null;
  feeTypeId: number;
  content: string;
  cost: number;
  status: number;
  accountDetail: any;
  schoolMst: string;
  transactionCode: string;
  billId: string;
  invoidId?: string;
  studentName: string;
  classYearName: string;
  schoolYear: string;
  signCode: string;
  invoiceStatus?: number;
  isEinvoice?: boolean;
  summaryBillId?: string;
  consolidatedBillIds?: string[];
}
interface InvoiceItemDetail {
  id: number;
  studentClassYearId: number;
  incomeSchoolId: number;
  unitPrice: number;
  unitType: number;
  expectedUnits: number;
  actualUnits: number;
  description: string;
  status: number;
  month: number;
  year: number;
  refundedAmount?: number;
  totalAmount?: number;
  createdDate: number[];
  updatedDate: number[];
  classYearName: string;
  studentName: string;
  schoolYear: string;
}

interface SchoolBillConfig {
  id?: number;
  schoolId: number;
  msthue: string;
  account: string;
  password?: string;
  invoiceType: number;
  symbol: string;
  number: number;
  mddkdoanh?: string;
  tddkdoanh?: string;
  dcddkdoanh?: string;
}



interface BillInfoProps {
  studentInvoiceId?: string | number;
  onSuccess?: () => void;
}

const ISSUED_INVOICE_MIN_STATUS = 5;
const BUYER_NAME_PREFIX = "BÁN CHO NGƯỜI TIÊU DÙNG";
const DEFAULT_XBILL_PAYLOAD = {
  mddkdoanh: "00999",
  tddkdoanh: "Ho kinh doanh CYBERLOTUS 999",
  dcddkdoanh: "1A Nguyen Cong Tru, Phường Hai Bà Trưng, Thành Phố Hà Nội",
  invoiceType: "2",
  symbol: "C26TCB",
  paymentMethod: 3,
  bankAccount: "12345689",
  creator: "ktt",
  taxCode: "10",
  itemTaxCode: "-1",
  currencyUnit: "VND",
  defaultItemName: "Dịch vụ",
};

export const BillInfo = ({
  studentInvoiceId: propStudentInvoiceId,
  onSuccess,
}: BillInfoProps) => {
  const { studentInvoiceId: paramStudentInvoiceId } = useParams();
  const studentInvoiceId = (propStudentInvoiceId || paramStudentInvoiceId)?.toString();
  const [incomes, setIncomes] = useState<InvoiceItemDetail[]>([]);
  const [incomeSchoolMap, setIncomeSchoolMap] = useState<Record<number, { name: string; type: number }>>({});
  const [student, setStudent] = useState<StudentData | null>(null);
  const [parents, setParents] = useState<ParentData[]>([]);
  const [currentClass, setCurrentClass] = useState<string>('');
  const [invoiceDetail, setInvoiceDetail] = useState<InvoiceDetail | null>(null);
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItemDetail[]>([]);
  const [incomeTypeMap, setIncomeTypeMap] = useState<Record<number, string>>({});
  const [incomeNameMap, setIncomeNameMap] = useState<Record<number, string>>({});
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [isDraftModalOpen, setIsDraftModalOpen] = useState(false);
  const [draftForm] = Form.useForm();
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [loadingViewDraft, setLoadingViewDraft] = useState(false);
  const [loadingSignDraft, setLoadingSignDraft] = useState(false);
  const [loadingViewSigned, setLoadingViewSigned] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [invoicePdfBase64, setInvoicePdfBase64] = useState<string | null>(null);

  const [billConfig, setBillConfig] = useState<SchoolBillConfig | null>(null);
  const [schoolId, setSchoolId] = useState<number | null>(null);
  const [remainingInvoiceCount, setRemainingInvoiceCount] = useState<number | null>(null);
  const [totalInvoiceCount, setTotalInvoiceCount] = useState<number | null>(null);
  const [siblingBills, setSiblingBills] = useState<any[]>([]);
  const [selectedSiblingIds, setSelectedSiblingIds] = useState<number[]>([]);
  const [loadingSiblings, setLoadingSiblings] = useState<boolean>(false);

  const fetchSiblingBills = async (invoiceId: string | number) => {
    try {
      setLoadingSiblings(true);
      const res: any = await getRequest(`/cms/invoice/sibling-invoices?studentInvoiceId=${invoiceId}`);
      if (res?.code === 200 && Array.isArray(res.info)) {
        setSiblingBills(res.info);
        // Mặc định tích chọn tất cả các khoản thu cùng tháng để gộp
        setSelectedSiblingIds(res.info.map((b: any) => b.studentInvoiceId));
      } else {
        setSiblingBills([]);
        setSelectedSiblingIds([]);
      }
    } catch (e) {
      console.error("Error fetching sibling bills:", e);
      setSiblingBills([]);
      setSelectedSiblingIds([]);
    } finally {
      setLoadingSiblings(false);
    }
  };

  const [loadingStudent, setLoadingStudent] = useState(false);
  const [loadingParents, setLoadingParents] = useState(false);

  const isXBillTokenExpired = (token: string | null): boolean => {
    if (!token) return true;
    try {
      const parts = token.split(".");
      if (parts.length === 3) {
        const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const payload = JSON.parse(window.atob(base64));
        if (typeof payload?.exp === "number") {
          return payload.exp * 1000 <= Date.now() + 60 * 1000;
        }
      }
    } catch (e) {
      // Nếu token không phải chuẩn JWT có exp thì kiểm tra tiếp qua phản hồi API
    }
    return false;
  };

  const getXBillToken = async (forceRefresh: boolean = false): Promise<string | null> => {
    const status = localStorage.getItem("xbill_status") || sessionStorage.getItem("xbill_status");
    const currentToken = localStorage.getItem("xbill_token") || sessionStorage.getItem("xbill_token");

    // 1. Nếu đã chủ động Đăng xuất hoặc chưa từng đăng nhập -> CHẶN 100%
    if (status !== "LOGGED_IN") {
      toast.error("Bạn chưa đăng nhập XBill. Vui lòng vào màn 'Thông tin cấu hình hóa đơn' để đăng nhập trước khi thao tác!");
      return null;
    }

    // 2. Nếu đang ở trạng thái LOGGED_IN, đã có token còn hạn và không bị buộc làm mới
    if (currentToken && !forceRefresh && !isXBillTokenExpired(currentToken)) {
      return currentToken;
    }

    // 3. Nếu status === 'LOGGED_IN' nhưng token bị thiếu hoặc hết hạn (cần làm mới ngầm)
    const targetSchoolId = schoolId || (localStorage.getItem('schoolId') ? parseInt(localStorage.getItem('schoolId')!, 10) : null);
    let config = billConfig;
    if (!config && targetSchoolId) {
      try {
        const configRes: any = await getRequest(`/cms/school-bill-config?schoolId=${targetSchoolId}`);
        if (configRes?.code === 200 && configRes.info) {
          config = configRes.info;
          setBillConfig(config);
        }
      } catch (e) {
        console.error("Error loading bill config for silent refresh:", e);
      }
    }

    if (config?.msthue && config?.account && config?.password) {
      try {
        const res: any = await postRequest("/token/2", {
          doanhnghiep_mst: config.msthue,
          username: config.account,
          password: config.password,
        });
        const newToken = res?.info?.result?.access_token;
        if (newToken) {
          localStorage.setItem("xbill_token", newToken);
          sessionStorage.setItem("xbill_token", newToken);
          localStorage.setItem("xbill_status", "LOGGED_IN");
          sessionStorage.setItem("xbill_status", "LOGGED_IN");
          localStorage.setItem("xbill_login_time", new Date().toLocaleString("vi-VN"));
          return newToken;
        }
      } catch (err) {
        console.error("Silent refresh XBill failed:", err);
      }
    }

    toast.error("Phiên đăng nhập XBill đã hết hạn. Vui lòng vào màn 'Thông tin cấu hình hóa đơn' để đăng nhập lại.");
    return null;
  };

  const postXBillWithAutoRefresh = async (
    url: string,
    body: any,
    initialToken: string
  ): Promise<any> => {
    let res = await postDirect(
      url,
      body,
      { Authorization: `Bearer ${initialToken}` },
      "json"
    );

    const errText = `${res?.message || ""} ${res?.msg || ""} ${typeof res?.info === "string" ? res.info : ""}`;
    const isTokenExpiredResponse =
      !res ||
      res?.status === 401 ||
      res?.status === 500 ||
      res?.code === 401 ||
      (res?.code !== 200 && /401|unauthorized|no body|unauthenticated/i.test(errText));

    if (isTokenExpiredResponse) {
      const refreshedToken = await getXBillToken(true);
      if (!refreshedToken) {
        return { _silentRefreshFailed: true };
      }
      res = await postDirect(
        url,
        body,
        { Authorization: `Bearer ${refreshedToken}` },
        "json"
      );
    }

    return res;
  };

  function tinhTongTienCoThue(tongTien: number, vat: number): number {
    switch (vat) {
      case -2: // Không kê khai
      case -1: // Không thuế
      case 0:  // Thuế 0%
        return tongTien;
      case 5:
        return tongTien * 1.05;
      case 10:
        return tongTien * 1.10;
      case 35: // Thuế 5% x 70%
        return tongTien * (1 + 0.05 * 0.7);
      case 7:  // Thuế 10% x 70%
        return tongTien * (1 + 0.10 * 0.7);
      case 8:
        return tongTien * 1.08;
      default:
        return tongTien;
    }
  }
  const formatBuyerName = (rawName?: string) => {
    const prefix = `${BUYER_NAME_PREFIX} - `;
    if (!rawName || !rawName.trim()) return BUYER_NAME_PREFIX;
    const trimmed = rawName.trim();
    return trimmed.startsWith(prefix) ? trimmed : `${prefix}${trimmed}`;
  };

  const handleViewSignedInvoice = async () => {
    try {
      if (!invoiceDetail) {
        toast.error("Chưa có thông tin hóa đơn");
        return;
      }

      const token = await getXBillToken();
      if (!token) return;

      if (!invoiceDetail.billId) {
        toast.error("Vui lòng tạo hóa đơn trước");
        return;
      }

      setLoadingViewSigned(true);
      const mst = invoiceDetail.schoolMst || billConfig?.msthue || "";
      const body = {
        doanhnghiep_mst: mst,
        ma_hoadon: invoiceDetail.summaryBillId || invoiceDetail.billId,
      };

      const res = await postXBillWithAutoRefresh("/token/28", body, token);
      if (res?._silentRefreshFailed) return;

      if (res?.code === 200 && res.info?.result?.base64pdf) {
        const pdfBase64 = res.info.result.base64pdf;
        const pdfUrl = `data:application/pdf;base64,${pdfBase64}`;

        setInvoicePdfBase64(pdfUrl);
        setIsInvoiceModalOpen(true);
      } else {
        toast.error(res?.info?.message || res?.msg || res?.message || "Không tải được hóa đơn đã ký");
      }
    } catch (err) {
      console.error("Error fetching signed invoice:", err);
      toast.error("Lỗi khi tải hóa đơn đã ký");
    } finally {
      setLoadingViewSigned(false);
    }
  };

  const handleDownloadd = () => {
    if (!invoicePdfBase64) return;

    const link = document.createElement("a");
    link.href = invoicePdfBase64;
    link.download = "invoice.pdf";
    link.click();
  };

  const handleViewInvoice = async () => {
    try {
      if (!invoiceDetail) {
        toast.error("Chưa có thông tin hóa đơn");
        return;
      }

      const token = await getXBillToken();
      if (!token) return;
      setLoadingViewDraft(true);
      const body = {
        doanhnghiep_mst: invoiceDetail.schoolMst,
        magiaodich: invoiceDetail.transactionCode || "",
        ma_hoadon: invoiceDetail.summaryBillId || invoiceDetail.billId,
      };

      const res = await postXBillWithAutoRefresh("/token/13", body, token);
      if (res?._silentRefreshFailed) return;

      if (res?.code === 200 && res.info?.result?.base64pdf) {
        const pdfBase64 = res.info.result.base64pdf;
        const pdfUrl = `data:application/pdf;base64,${pdfBase64}`;

        setPdfBase64(pdfUrl);
        setIsModalOpen(true);
      } else {
        toast.error("Không tải được hóa đơn nháp");
      }
    } catch (err) {
      console.error("Error fetching draft invoice:", err);
      toast.error("Lỗi khi tải hóa đơn nháp");
    } finally {
      setLoadingViewDraft(false);
    }
  };

  const handleSignDraftInvoice = async () => {
    try {
      if (!invoiceDetail) {
        toast.error("Chưa có thông tin hóa đơn");
        return;
      }

      if (remainingInvoiceCount === null || remainingInvoiceCount === 0) {
        toast.error("Trường đã hết hóa đơn. Vui lòng mua thêm.");
        return;
      }

      const token = await getXBillToken();
      if (!token) return;
      if (!invoiceDetail.transactionCode) {
        toast.error("Vui lòng tạo hóa đơn nháp trước");
        return;
      }
      if (invoiceDetail.signCode === "01") {
        toast.error("Hóa đơn đã ký");
        return;
      }

      setLoadingSignDraft(true);
      const mst = invoiceDetail.schoolMst || billConfig?.msthue || "";
      const body = {
        doanhnghiep_mst: mst,
        magiaodich: invoiceDetail.transactionCode || "",
        ma_hoadon: invoiceDetail.summaryBillId || invoiceDetail.invoidId || invoiceDetail.billId,
      };

      const res = await postXBillWithAutoRefresh("/token/27", body, token);
      if (res?._silentRefreshFailed) return;

      if (res?.code === 200) {
        toast.success("Ký hóa đơn nháp thành công");
        fetchInvoiceDetail();
        if (schoolId) {
          fetchBillConfig(schoolId);
        }
        onSuccess?.();
      } else {
        toast.error(res?.message || "Không ký được hóa đơn nháp");
      }
    } catch (err) {
      console.error("Error signing draft invoice:", err);
      toast.error("Lỗi khi ký hóa đơn nháp");
    } finally {
      setLoadingSignDraft(false);
    }
  };


  const handleDownload = () => {
    if (!pdfBase64) return;

    const link = document.createElement("a");
    link.href = `${pdfBase64}`;
    link.download = "invoice.pdf";
    link.click();
  };


  const handleCreateDraftInvoice = async () => {
    try {
      const token = await getXBillToken();
      if (!token) return;
      const values = await draftForm.validateFields();
      setLoadingDraft(true);


      const invoiceRes = await getRequest(`/cms/accountant/student-invoice/${studentInvoiceId}`);
      const info = invoiceRes?.info;
      const detailRes = await getRequest(`/cms/accountant/income-student-register/group/${studentInvoiceId}`);
      let allDetails = detailRes?.info ?? [];

      // Xử lý gộp khoản thu cùng tháng nếu kế toán tích chọn
      const activeSiblingBills = siblingBills.filter(b => selectedSiblingIds.includes(b.studentInvoiceId));
      const targetSubBillIds: string[] = [info?.billId].filter(Boolean);

      if (activeSiblingBills.length > 0) {
        activeSiblingBills.forEach(sib => {
          if (sib.billId) targetSubBillIds.push(sib.billId);
        });
        const siblingDetailResults = await Promise.all(
          activeSiblingBills.map(sib =>
            getRequest(`/cms/accountant/income-student-register/group/${sib.studentInvoiceId}`).catch(err => {
              console.error(`Error fetching items for sibling bill ${sib.studentInvoiceId}:`, err);
              return null;
            })
          )
        );
        siblingDetailResults.forEach(sibDetailRes => {
          if (sibDetailRes?.info && Array.isArray(sibDetailRes.info)) {
            allDetails = [...allDetails, ...sibDetailRes.info];
          }
        });
      }

      const dschitiet = allDetails.map((item: any, index: number) => {
        const soLuong = item.actualUnits && item.actualUnits > 0 ? item.actualUnits : 1;
        const tongTien = item.totalAmount ?? ((item.unitPrice ?? 0) * soLuong);
        const dongia = item.unitPrice ?? (soLuong > 0 ? tongTien / soLuong : tongTien);
        return {
          stt: index + 1,
          hanghoa_loai: 1,
          ma: "",
          ten: item.description ?? DEFAULT_XBILL_PAYLOAD.defaultItemName,
          donvitinh: DEFAULT_XBILL_PAYLOAD.currencyUnit,
          soluong: soLuong,
          dongia: dongia,
          tongtien_chuathue: tongTien,
          mathue: DEFAULT_XBILL_PAYLOAD.itemTaxCode,
          tongtien_cothue: tinhTongTienCoThue(tongTien, -1),
          tyletinhthue: 1,
        };
      });

      const totalCalculated = dschitiet.reduce((sum: number, it: any) => sum + (it.tongtien_chuathue ?? 0), 0);
      const finalTotal = totalCalculated > 0
        ? totalCalculated
        : ((invoiceDetail?.cost ?? 0) + activeSiblingBills.reduce((s, b) => s + (b.cost ?? 0), 0));

      const now = new Date();
      const ngaylapFull = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;
      let currentKyHieu = billConfig?.symbol;

      if (!currentKyHieu && schoolId) {
        try {
          const configRes = await getRequest(`/cms/school-bill-config?schoolId=${schoolId}`);
          if (configRes?.code === 200 && configRes.info) {
            currentKyHieu = configRes.info.symbol;
            setBillConfig(configRes.info);
          }
        } catch (e) {
          console.error("Error fetching bill config in draft creation:", e);
        }
      }

      const payload: any = {
        mddkdoanh: billConfig?.mddkdoanh || DEFAULT_XBILL_PAYLOAD.mddkdoanh,
        tddkdoanh: billConfig?.tddkdoanh || DEFAULT_XBILL_PAYLOAD.tddkdoanh,
        dcddkdoanh: billConfig?.dcddkdoanh || DEFAULT_XBILL_PAYLOAD.dcddkdoanh,
        mchang: "",
        tchang: "",
        doanhnghiep_mst: billConfig?.msthue || info?.schoolMst,
        loaihoadon_ma: billConfig?.invoiceType?.toString() || DEFAULT_XBILL_PAYLOAD.invoiceType,
        mauso: billConfig?.invoiceType?.toString() || DEFAULT_XBILL_PAYLOAD.invoiceType,
        kyhieu: currentKyHieu || billConfig?.symbol || DEFAULT_XBILL_PAYLOAD.symbol,
        ma_hoadon: info?.billId,
        ngaylap: ngaylapFull,
        dnmua_mst: "",
        dnmua_ten: "",
        dnmua_tennguoimua: formatBuyerName(values.dnmua_tennguoimua),
        dnmua_diachi: values.dnmua_diachi,
        dnmua_sdt: "",
        dnmua_email: "",
        thanhtoan_phuongthuc: values.thanhtoan_phuongthuc ?? DEFAULT_XBILL_PAYLOAD.paymentMethod,
        thanhtoan_phuongthuc_ten: "",
        thanhtoan_taikhoan: DEFAULT_XBILL_PAYLOAD.bankAccount,
        thanhtoan_nganhang: "",
        tongtien_chuavat: finalTotal,
        tienthue: 0,
        tongtien_covat: finalTotal,
        nguoilap: DEFAULT_XBILL_PAYLOAD.creator,
        dschitiet,
        dsthuesuat: [
          {
            mathue: DEFAULT_XBILL_PAYLOAD.taxCode,
            tongtien_chiuthue: finalTotal,
            tongtien_thue: 0,
          },
        ],
        nghiquyetapdung: 0,
      };

      if (targetSubBillIds.length > 1) {
        payload.subBillIds = targetSubBillIds;
      }

      const res = await postXBillWithAutoRefresh("/token/3", payload, token);
      if (res?._silentRefreshFailed) return;
      if (res?.code === 200) {
        toast.success("Tạo hóa đơn nháp thành công");
        setIsDraftModalOpen(false);
        fetchInvoiceDetail();
        if (studentInvoiceId) {
          fetchInvoiceItems(studentInvoiceId);
        }
        onSuccess?.();
      } else {
        const errMsg = res?.info?.result?.motaketqua || res?.msg || "Không thể tạo hóa đơn nháp";
        toast.error(errMsg);
      }
    } catch (err) {
      console.error(err);
      toast.error("Lỗi khi tạo hóa đơn nháp");
    } finally {
      setLoadingDraft(false);
    }
  };

  const fetchInvoiceDetail = async () => {
    const res: ResponseDataType<InvoiceDetail> = await getRequest(
      `/cms/accountant/student-invoice/${studentInvoiceId}`
    );
    if (res?.code === 200 && res.info) {
      setInvoiceDetail(res.info);
      const buyerClass = res.info.classYearName || "";
      const buyerSchoolYear = res.info.schoolYear || "";
      const buyerAddress = buyerClass && buyerSchoolYear
        ? `${buyerClass} - ${buyerSchoolYear}`
        : buyerClass || buyerSchoolYear || "";
      draftForm.setFieldsValue({
        dnmua_tennguoimua: formatBuyerName(res.info.studentName),
        dnmua_diachi: buyerAddress,
      });
    } else {
      toast.error('Không thể tải thông tin hóa đơn');
    }
  };
  const fetchIncomeSchoolName = async (id: number) => {
    if (incomeNameMap[id]) return;

    const res: ResponseDataType<{ id: number; name: string }> = await getRequest(
      `/cms/accountant/income-school/${id}`
    );

    if (res?.code === 200 && res.info && typeof res.info.name === 'string') {
      setIncomeNameMap(prev => ({ ...prev, [id]: res.info!.name }));
    }
  };

  const fetchInvoiceItems = async (studentInvoiceId: string) => {
    const res: ResponseDataType<InvoiceItemDetail[]> = await getRequest(
      `/cms/accountant/income-student-register/group/${studentInvoiceId}`
    );
    if (res?.code === 200 && Array.isArray(res.info)) {
      setInvoiceItems(res.info);
    } else {
      toast.error('Không thể tải chi tiết hóa đơn');
    }
  };

  const fetchStudent = async (id: number) => {
    setLoadingStudent(true);
    const res: ResponseDataType<StudentData> = await getRequest(`/cms/student/${id}`);
    if (res?.code === 200 && res.info) {
      setStudent(res.info);
      draftForm.setFieldsValue({
        dnmua_tennguoimua: formatBuyerName(res.info.name),

      });
    } else {
      setStudent(null);
      toast.error('Không thể tải thông tin học sinh');
    }
    setLoadingStudent(false);
  };

  const fetchCurrentClass = async (id: number) => {
    const res: ResponseDataType<any> = await getRequest(
      `/cms/student/${id}/get-classYear-by-student?status=1`
    );
    if (res?.code === 200 && Array.isArray(res.info) && res.info.length > 0) {
      setCurrentClass(res.info[0].className || '');
    } else {
      setCurrentClass('');
    }
  };

  useEffect(() => {
    const fetchIncomeSchoolInfo = async () => {
      const uniqueIds = Array.from(new Set(invoiceItems.map(item => item.incomeSchoolId)));
      const newMap: Record<number, { name: string; type: number }> = {};
      const nameMap: Record<number, string> = {};

      for (const id of uniqueIds) {
        try {
          const res: ResponseDataType<{ name: string; type: number; schoolId: number }> = await getRequest(`/cms/accountant/income-school/${id}`);
          if (res.code === 200 && res.info) {
            newMap[id] = {
              name: res.info.name,
              type: res.info.type,
            };
            nameMap[id] = res.info.name;
            if (id === invoiceItems[0]?.incomeSchoolId) {
              setSchoolId(res.info.schoolId);
              fetchBillConfig(res.info.schoolId);
            }
          }
        } catch {
          newMap[id] = { name: 'Không xác định', type: -1 };
          nameMap[id] = 'Không xác định';
        }
      }

      setIncomeSchoolMap(newMap);
      setIncomeNameMap(nameMap);
    };

    if (invoiceItems.length > 0) {
      fetchIncomeSchoolInfo();
    }
  }, [invoiceItems]);

  const fetchStudentParents = async (id: number) => {
    setLoadingParents(true);
    const response: ResponseDataType<ParentData[]> = await getRequest(
      `/cms/student/get-parent-by-student?studentId=${id}`
    );
    if (response?.code === 200 && Array.isArray(response.info)) {
      setParents(response.info);
    } else {
      setParents([]);
      toast.error('Không thể tải danh sách phụ huynh');
    }
    setLoadingParents(false);
  };

  const fetchBillConfig = async (schoolId: number) => {
    try {
      const res = await getRequest(`/cms/school-bill-config?schoolId=${schoolId}`);
      if (res?.code === 200 && res.info) {
        setBillConfig(res.info);
        if (res.info.symbol) {
          const currentYear = new Date().getFullYear();
          const summaryRes = await getRequest(`/cms/school-bill-config/invoice-usage-summary?schoolId=${schoolId}&year=${currentYear}&invoiceSymbol=${res.info.symbol}`);
          if (summaryRes?.code === 200 && summaryRes.info) {
            setRemainingInvoiceCount(summaryRes.info.remainingQuantity);
            setTotalInvoiceCount(summaryRes.info.totalPurchased);
          }
        }
      } else {
        setBillConfig(null);
      }
    } catch (error) {
      console.error("Error fetching bill config:", error);
    }
  };


  const fetchInvoiceQr = async (studentId: number, studentInvoiceId: number) => {
    try {
      const res: ResponseDataType<string> = await getRequest(
        `/cms/accountant/generate-qr-payment?studentId=${studentId}&listInvoiceIds=${studentInvoiceId}`
      );

      if (res?.code === 200 && res.info) {
        setQrUrl(res.info);
      } else {
        toast.error("Không thể tạo QR cho hóa đơn");
      }
    } catch (error) {
      toast.error("Lỗi khi tạo QR");
    }
  };

  const handleConfirmCashPayment = () => {
    Modal.confirm({
      title: 'Xác nhận thanh toán',
      content: 'Bạn có chắc chắn muốn xác nhận học sinh đã thanh toán bằng tiền mặt không?',
      okText: 'Xác nhận',
      cancelText: 'Hủy',
      onOk: handleCashPayment,
    });
  };

  const handleCashPayment = async () => {
    try {
      const res = await postRequest(`/cms/invoice/cash-payment?studentInvoiceId=${studentInvoiceId}`);
      if (res?.code === 200) {
        toast.success('Xác nhận thanh toán thành công');
        fetchInvoiceDetail();
        onSuccess?.();
      } else {
        toast.error(res?.msg || 'Lỗi khi xác nhận thanh toán');
      }
    } catch (error) {
      console.error('Error cash payment:', error);
      toast.error('Lỗi khi call API thanh toán tiền mặt');
    }
  };

  useEffect(() => {
    setQrUrl(null);
    if (studentInvoiceId) {
      fetchInvoiceDetail();
      fetchInvoiceItems(studentInvoiceId);
    }
  }, [studentInvoiceId]);

  useEffect(() => {
    if (invoiceDetail?.studentId) {
      fetchStudent(invoiceDetail.studentId);
      fetchStudentParents(invoiceDetail.studentId);
      fetchCurrentClass(invoiceDetail.studentId);
    }
    if (invoiceDetail?.studentInvoiceId && invoiceDetail?.status === 0) {
      fetchInvoiceQr(invoiceDetail.studentId, invoiceDetail.studentInvoiceId);
    } else {
      setQrUrl(null);
    }
  }, [invoiceDetail]);

  return (
    <div className="bill-content">
      {invoiceDetail && (
        <div
          className="invoice-info"
          style={{
            marginBottom: 24,
            padding: 16,
            backgroundColor: '#f5f8fa',
            borderRadius: 8,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0 }}>Thông tin hóa đơn</h2>
              {invoiceDetail.isEinvoice === false && (
                <Tag color="default" style={{ fontSize: "14px", padding: "2px 8px" }}>
                  Phiếu thu nội bộ (Không xuất HĐĐT)
                </Tag>
              )}
              {invoiceDetail.summaryBillId && invoiceDetail.summaryBillId !== invoiceDetail.billId && (
                <Tag color="purple" style={{ fontSize: "14px", padding: "2px 8px" }}>
                  Đã gộp vào HĐ: <strong>{invoiceDetail.summaryBillId}</strong>
                </Tag>
              )}
              {invoiceDetail.summaryBillId && invoiceDetail.summaryBillId === invoiceDetail.billId && invoiceDetail.consolidatedBillIds && invoiceDetail.consolidatedBillIds.length > 1 && (
                <Tag color="blue" style={{ fontSize: "14px", padding: "2px 8px" }}>
                  HĐĐT gộp: <strong>{invoiceDetail.consolidatedBillIds.join(', ')}</strong>
                </Tag>
              )}
              {remainingInvoiceCount !== null && (
                <Tag
                  color={
                    remainingInvoiceCount > 10
                      ? "blue"
                      : remainingInvoiceCount > 0
                        ? "orange"
                        : "red"
                  }
                  style={{ fontSize: "14px", padding: "2px 8px" }}
                >
                  Số hóa đơn có thể xuất: <strong>{remainingInvoiceCount}/{totalInvoiceCount || 0}</strong>
                </Tag>
              )}
            </div>
            <p style={{ marginTop: 8 }}><b>Nội dung:</b> {invoiceDetail.content}</p>
            <p><b>Số tiền:</b> {invoiceDetail.cost.toLocaleString()} VND</p>
            <p><b>Trạng thái:</b> {invoiceDetail.status === -1 ? 'Chờ duyệt' : invoiceDetail.status === 0 ? 'Chưa thanh toán' : 'Đã thanh toán'}</p>
            {invoiceDetail.status === 0 && (
              <Button
                type="primary"
                style={{ marginTop: 10 }}
                onClick={handleConfirmCashPayment}
              >
                Đã thanh toán bằng tiền mặt
              </Button>
            )}
            {invoiceDetail.status === 1 && invoiceDetail.isEinvoice !== false && (
              <>
                <Button
                  type="primary"
                  onClick={() => {
                    const status = localStorage.getItem("xbill_status") || sessionStorage.getItem("xbill_status");
                    if (status !== "LOGGED_IN") {
                      toast.error("Bạn chưa đăng nhập XBill. Vui lòng vào màn 'Thông tin cấu hình hóa đơn' để đăng nhập trước khi tạo hóa đơn!");
                      return;
                    }
                    draftForm.resetFields();
                    setIsDraftModalOpen(true);
                    fetchInvoiceDetail();
                    if (studentInvoiceId) {
                      fetchSiblingBills(studentInvoiceId);
                    }
                    const buyerClass = invoiceDetail?.classYearName || "";
                    const buyerSchoolYear = invoiceDetail?.schoolYear || "";
                    const buyerAddress = buyerClass && buyerSchoolYear
                      ? `${buyerClass} - ${buyerSchoolYear}`
                      : buyerClass || buyerSchoolYear || "";
                    draftForm.setFieldsValue({
                      dnmua_tennguoimua: formatBuyerName(invoiceDetail?.studentName),
                      dnmua_diachi: buyerAddress,
                      thanhtoan_phuongthuc: 2,
                    });
                  }}
                  disabled={(invoiceDetail?.invoiceStatus || 0) >= ISSUED_INVOICE_MIN_STATUS || Boolean(invoiceDetail?.summaryBillId && invoiceDetail?.summaryBillId !== invoiceDetail?.billId)}
                >
                  Tạo hóa đơn nháp
                </Button>
                <Button
                  type="primary"
                  style={{ marginLeft: 10 }}
                  onClick={handleViewInvoice}
                  loading={loadingViewDraft}
                  disabled={(invoiceDetail?.invoiceStatus || 0) >= ISSUED_INVOICE_MIN_STATUS}
                >
                  Xem hóa đơn nháp
                </Button>

                <Modal
                  open={isModalOpen}
                  onCancel={() => setIsModalOpen(false)}
                  width={800}
                  footer={[
                    <Button key="download" type="primary" onClick={handleDownload}>
                      Tải về
                    </Button>,
                    <Button key="close" onClick={() => setIsModalOpen(false)}>
                      Đóng
                    </Button>,
                  ]}
                >
                  {pdfBase64 && (
                    <iframe
                      title="Hóa đơn nháp"
                      src={pdfBase64}
                      width="100%"
                      height="600px"
                    />
                  )}
                </Modal>
                <Button
                  type="primary"
                  style={{ marginLeft: 10 }}
                  onClick={handleSignDraftInvoice}
                  loading={loadingSignDraft}
                  disabled={(invoiceDetail?.invoiceStatus || 0) >= ISSUED_INVOICE_MIN_STATUS || Boolean(invoiceDetail?.summaryBillId && invoiceDetail?.summaryBillId !== invoiceDetail?.billId)}
                >
                  Ký hóa đơn nháp
                </Button>
                <Button
                  type="primary"
                  style={{ marginLeft: 10 }}
                  onClick={handleViewSignedInvoice}
                  loading={loadingViewSigned}
                >
                  Xem hóa đơn đã ký
                </Button>


                <Modal
                  open={isInvoiceModalOpen}
                  onCancel={() => setIsInvoiceModalOpen(false)}
                  width={800}
                  footer={[
                    <Button key="download" type="primary" onClick={handleDownloadd}>
                      Tải về
                    </Button>,
                    // <Button
                    //   key="print"
                    //   onClick={() => {
                    //     if (invoicePdfBase64) {
                    //       const printWindow = window.open(invoicePdfBase64);
                    //       if (printWindow) {
                    //         printWindow.addEventListener("load", () => {
                    //           printWindow.print();
                    //         });
                    //       }
                    //     }
                    //   }}
                    // >
                    //   In hóa đơn
                    // </Button>,
                    <Button key="close" onClick={() => setIsInvoiceModalOpen(false)}>
                      Đóng
                    </Button>,
                  ]}
                >
                  {invoicePdfBase64 && (
                    <iframe title="Hóa đơn đã ký" src={invoicePdfBase64} width="100%" height="600px" />
                  )}
                </Modal>
                </>
            )}
            {/* <Button
              type="dashed"
              onClick={async () => {
                try {
                  if (!invoiceDetail) {
                    toast.error("Chưa có thông tin hóa đơn");
                    return;
                  }

                  const token = await getXBillToken();
                  if (!token) return;

                  // Lấy thông tin cần thiết từ invoiceDetail
                  const body = {
                    doanhnghiep_mst: invoiceDetail.schoolMst,
                    magiaodich: invoiceDetail.transactionCode,
                    ma_hoadon: invoiceDetail.billId,
                  };

                  const res = await postDirect('/token/13',
                    body,
                    { Authorization: `Bearer ${token}` },
                    "json" // trả về JSON chứa base64
                  );

                  if (res?.code === 200 && res.info) {
                    const pdfBase64 = res.info; // backend trả về chuỗi base64 pdf
                    const pdfUrl = `data:application/pdf;base64,${pdfBase64}`;
                    window.open(pdfUrl, "_blank"); // mở PDF trên tab mới
                  } else {
                    toast.error("Không tải được hóa đơn nháp");
                  }
                } catch (err) {
                  console.error("Error fetching draft invoice:", err);
                  toast.error("Lỗi khi tải hóa đơn nháp");
                }
              }}
            >
              Xem hóa đơn nháp
            </Button>
 */}


          </div>

          {qrUrl && invoiceDetail.status === 0 && (
            <div className="qr-section" style={{ textAlign: 'center', marginRight: 100 }}>
              <p style={{ fontWeight: 'bold', marginBottom: 8 }}>QR Thanh toán</p>
              <img
                src={`data:image/png;base64,${qrUrl}`}
                alt="QR Code"
                style={{ width: 200, height: 200 }}
              />
            </div>
          )}
        </div>
      )}



      <Card className="invoice-details" style={{
        marginTop: 0

      }}>

        <h2 style={{ marginTop: 10 }}>Chi tiết hóa đơn</h2>
        <Table
          dataSource={invoiceItems}
          pagination={false}
          columns={[
            {
              title: 'Tên nguồn thu',
              dataIndex: 'incomeSchoolId',
              render: (id: number, record: InvoiceItemDetail) => {
                if (record.description && record.description.trim().toLowerCase().startsWith('nợ')) {
                  return record.description;
                }
                if (incomeNameMap[id]) {
                  return incomeNameMap[id];
                }
                if (incomeSchoolMap[id]?.name) {
                  return incomeSchoolMap[id].name;
                }
                if (id) {
                  fetchIncomeSchoolName(id);
                  return 'Đang tải...';
                }
                return record.description || 'Khoản thu';
              },
            },
            {
              title: 'Mô tả',
              dataIndex: 'description',
              render: (val: string, record: InvoiceItemDetail) => val || record.description || '—',
            },
            {
              title: 'Đơn giá',
              dataIndex: 'unitPrice',
              render: val => (typeof val === 'number' ? `${val.toLocaleString()} đ` : '—'),
            },
            {
              title: 'Số lượng',
              key: 'units',
              render: (record: InvoiceItemDetail) => {
                const qty = record.actualUnits && record.actualUnits > 0
                  ? record.actualUnits
                  : (record.expectedUnits && record.expectedUnits > 0 ? record.expectedUnits : 1);
                return qty;
              },
            },
            {
              title: 'Thành tiền',
              key: 'totalAmount',
              render: (record: InvoiceItemDetail) => {
                if (typeof record.totalAmount === 'number') {
                  return `${record.totalAmount.toLocaleString()} đ`;
                }
                const qty = record.actualUnits && record.actualUnits > 0
                  ? record.actualUnits
                  : (record.expectedUnits && record.expectedUnits > 0 ? record.expectedUnits : 1);
                const amount = (record.unitPrice ?? 0) * qty;
                return `${amount.toLocaleString()} đ`;
              },
            },
            { title: 'Tháng', dataIndex: 'month' },
            { title: 'Năm học', dataIndex: 'schoolYear' },
            { title: 'Lớp', dataIndex: 'classYearName' },
          ]}
          rowKey={record => record.id}
        />

      </Card>
      <div className="info-container" style={{ display: 'flex', gap: 24, marginTop: 24, flexWrap: 'wrap' }}>
        {/* Bên trái: Thông tin học sinh */}
        <div className="student-info" style={{ flex: 1, background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3>Thông tin học sinh</h3>
          <Spin spinning={loadingStudent}>
            <Table
              dataSource={
                student
                  ? [
                    { label: 'Họ tên', value: student.name },
                    { label: 'Ngày sinh', value: student.dob },
                    { label: 'Giới tính', value: student.gender === 0 ? 'Nữ' : 'Nam' },
                    { label: 'Email', value: student.email },
                    { label: 'Địa chỉ', value: student.address },
                    { label: 'CMND/CCCD', value: student.cmnd },
                    { label: 'Mã BHYT', value: student.bhytNum },
                    { label: 'Lớp đang học', value: currentClass },
                  ]
                  : []
              }
              pagination={false}
              columns={[
                { dataIndex: 'label', width: '30%' },
                { dataIndex: 'value' },
              ]}
              rowKey={(record) => record.label}
              size="small"
              bordered
            />
          </Spin>
        </div>


        {/* Bên phải: Phụ huynh */}
        <div className="parent-info parent-info-section" style={{ flex: 1, background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3>Phụ huynh</h3>
          <Table
            dataSource={parents}
            pagination={false}
            columns={[
              { title: 'Họ tên', dataIndex: 'name' },
              { title: 'Email', dataIndex: 'email' },
              { title: 'SĐT', dataIndex: 'phoneNumber' },
            ]}
            rowKey={record => record?.parentId?.toString() ?? `parent-${Math.random()}`}
            size="small"
            bordered
          />
        </div>
      </div>
      {/* Modal đăng nhập */}
      <Modal
        title="Tạo hóa đơn nháp"
        open={isDraftModalOpen}
        onCancel={() => setIsDraftModalOpen(false)}
        onOk={handleCreateDraftInvoice}
        confirmLoading={loadingDraft}
        okText="Tạo"
        cancelText="Hủy"
        width={siblingBills.length > 0 ? 640 : 520}
      >
        <Form form={draftForm} layout="vertical">
          <Form.Item name="dnmua_tennguoimua" label="Tên người mua" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="dnmua_diachi" label="Địa chỉ người mua" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="thanhtoan_phuongthuc"
            label="Phương thức thanh toán"
            rules={[{ required: true, message: "Vui lòng chọn phương thức thanh toán" }]}
          >
            <Select placeholder="Chọn phương thức thanh toán">
              <Select.Option value={1}>Tiền mặt</Select.Option>
              <Select.Option value={2}>Chuyển khoản</Select.Option>
              <Select.Option value={3}>Tiền mặt/Chuyển khoản</Select.Option>
            </Select>
          </Form.Item>

          <Spin spinning={loadingSiblings}>
            {siblingBills.length > 0 && (
              <div style={{ marginTop: 16, padding: 12, backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontWeight: 600, color: "#166534", fontSize: 13 }}>
                  Gộp các khoản thu cùng tháng ({siblingBills[0]?.month}/{siblingBills[0]?.year})
                </span>
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, height: "auto", fontSize: 12 }}
                  onClick={() => {
                    if (selectedSiblingIds.length === siblingBills.length) {
                      setSelectedSiblingIds([]);
                    } else {
                      setSelectedSiblingIds(siblingBills.map(b => b.studentInvoiceId));
                    }
                  }}
                >
                  {selectedSiblingIds.length === siblingBills.length ? "Bỏ chọn tất cả" : "Chọn tất cả"}
                </Button>
              </div>
              <div style={{ fontSize: 12, color: "#4b5563", marginBottom: 8 }}>
                Tích chọn các khoản thu để gộp chung vào 1 HĐĐT (tiết kiệm số hóa đơn đã mua):
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 180, overflowY: "auto" }}>
                {siblingBills.map((sib) => {
                  const isChecked = selectedSiblingIds.includes(sib.studentInvoiceId);
                  return (
                    <div
                      key={sib.studentInvoiceId}
                      onClick={() => {
                        setSelectedSiblingIds(prev =>
                          isChecked ? prev.filter(id => id !== sib.studentInvoiceId) : [...prev, sib.studentInvoiceId]
                        );
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "8px 12px",
                        backgroundColor: isChecked ? "#fff" : "#f9fafb",
                        border: isChecked ? "1px solid #10b981" : "1px solid #e5e7eb",
                        borderRadius: 6,
                        cursor: "pointer",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Checkbox checked={isChecked} />
                        <div>
                          <span style={{ fontWeight: 600, color: "#1f2937", fontSize: 13 }}>
                            {sib.content || sib.invoidId || "Khoản thu"}
                          </span>
                          {sib.billId && (
                            <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 6 }}>
                              ({sib.billId})
                            </span>
                          )}
                        </div>
                      </div>
                      <span style={{ fontWeight: 700, color: "#059669", fontSize: 13 }}>
                        {new Intl.NumberFormat("vi-VN").format(sib.cost || 0)} VNĐ
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            )}
          </Spin>
        </Form>
      </Modal>

    </div>
  );
};
